import React from "react"
import { RiMenuAddLine, RiBookmarkLine, RiBookmarkFill } from "@remixicon/react"
import { LearningResource } from "api"
import {
  LocalDate,
  DEFAULT_RESOURCE_IMG,
  getLearningResourcePrices,
  getBestResourceStartDate,
  showStartAnytime,
  getResourceLanguage,
  resourceContentFilesImageSrc,
  useImageWithFallback,
} from "ol-utilities"
import type { Size } from "../Card/Card"
import type { PushUrl } from "../LinkAdapter/LinkAdapter"
import { BaseLearningResourceCard } from "../BaseLearningResourceCard/BaseLearningResourceCard"
import type { ActionButtonInfo } from "../BaseLearningResourceCard/BaseLearningResourceCard"
import { LearningResourceListCard } from "./LearningResourceListCard"

type ResourceIdCallback = (
  event: React.MouseEvent<HTMLButtonElement>,
  resourceId: number,
) => void

interface LearningResourceCardProps {
  isLoading?: boolean
  resource?: LearningResource | null
  className?: string
  size?: Size
  href?: string
  /**
   * If set, a plain click pushes this URL with window.history.pushState
   * rather than navigating to href. See LinkAdapter for the full rules.
   */
  pushUrl?: PushUrl
  onAddToLearningPathClick?: ResourceIdCallback | null
  onAddToUserListClick?: ResourceIdCallback | null
  inUserList?: boolean
  inLearningPath?: boolean
  onClick?: React.MouseEventHandler
  headingLevel?: number
  list?: boolean
}

const LearningResourceCard: React.FC<LearningResourceCardProps> = ({
  isLoading,
  resource,
  className,
  size = "medium",
  href,
  pushUrl,
  onAddToLearningPathClick,
  onAddToUserListClick,
  inLearningPath,
  inUserList,
  onClick,
  headingLevel = 6,
  list = false,
}) => {
  const {
    src: imageSrc,
    unoptimized: imageUnoptimized,
    onError: onImageError,
  } = useImageWithFallback(
    // won't try contentFile image if resource image 404s, but that matches
    // existing behavior: contentFile is only tried when resource has no image URL
    resource?.image?.url ??
      (resource ? resourceContentFilesImageSrc(resource) : null),
    DEFAULT_RESOURCE_IMG,
  )

  if (list) {
    return (
      <LearningResourceListCard
        isLoading={isLoading}
        resource={resource}
        className={className}
        href={href}
        pushUrl={pushUrl}
        onAddToLearningPathClick={onAddToLearningPathClick}
        onAddToUserListClick={onAddToUserListClick}
        inUserList={inUserList}
        inLearningPath={inLearningPath}
        onClick={onClick}
        headingLevel={headingLevel}
      />
    )
  }

  if (isLoading) {
    return (
      <BaseLearningResourceCard
        isLoading
        className={className}
        size={size}
        headingLevel={headingLevel}
      />
    )
  }

  if (!resource) {
    return null
  }

  const prices = getLearningResourcePrices(resource)
  const anytime = showStartAnytime(resource)
  const startDate = getBestResourceStartDate(resource)
  const format = size === "small" ? "MMM DD, YYYY" : "MMMM DD, YYYY"
  const formattedDate = anytime
    ? "Anytime"
    : startDate && <LocalDate date={startDate} format={format} />

  const showStartLabel = size !== "small" || anytime
  const startLabel = showStartLabel ? "Starts: " : undefined

  const actions: ActionButtonInfo[] = []

  if (onAddToLearningPathClick) {
    actions.push({
      onClick: (event) => onAddToLearningPathClick(event, resource.id),
      "aria-label": "Add to Learning Path",
      filled: inLearningPath,
      icon: <RiMenuAddLine aria-hidden />,
    })
  }

  if (onAddToUserListClick) {
    actions.push({
      onClick: (event) => onAddToUserListClick(event, resource.id),
      "aria-label": `Bookmark ${resource.resource_category}`,
      filled: inUserList,
      icon: inUserList ? (
        <RiBookmarkFill aria-hidden />
      ) : (
        <RiBookmarkLine aria-hidden />
      ),
    })
  }

  return (
    <BaseLearningResourceCard
      className={className}
      size={size}
      href={href}
      pushUrl={pushUrl}
      onClick={onClick}
      headingLevel={headingLevel}
      imageSrc={imageSrc}
      imageAlt={resource.image?.alt ?? ""}
      onImageError={onImageError}
      imageUnoptimized={imageUnoptimized}
      title={resource.title}
      resourceType={resource.resource_category}
      resourcePrice={prices.course.display}
      certificatePrice={prices.certificate.display}
      hasCertificate={resource.certification}
      startLabel={startLabel}
      startDate={formattedDate}
      actions={actions}
      lang={getResourceLanguage(resource)}
      ariaLabel={`${resource.resource_category}: ${resource.title}`}
    />
  )
}

export { LearningResourceCard }
export type { LearningResourceCardProps }
